import {
  PREVENTION_CONTROL_TYPES,
  type NetworkControlSpec,
  type PreventionControlSpec,
  type PreventionProvider,
} from './prevention.schema';

export const NETWORK_APPLY_MODELS = {
  REVOKE_INGRESS: 'revokeIngress',
  CREATE_DENY_RULE: 'createDenyRule',
} as const;

export type NetworkApplyModel =
  (typeof NETWORK_APPLY_MODELS)[keyof typeof NETWORK_APPLY_MODELS];

export type NetworkControlDirection = NetworkControlSpec['direction'];

type NetworkProviderCapabilityProfile = {
  applyModel: NetworkApplyModel;
  requiredScopeType: string | null;
  requiresScopeId: boolean;
  supportedDirections: readonly NetworkControlDirection[];
  constraints: readonly string[];
};

const AWS_SECURITY_GROUP_PROFILE: NetworkProviderCapabilityProfile = {
  applyModel: NETWORK_APPLY_MODELS.REVOKE_INGRESS,
  requiredScopeType: 'securityGroup',
  requiresScopeId: false,
  supportedDirections: ['inbound', 'outbound'],
  constraints: [
    'AWS security group prevention revokes an existing allow rule; it does not create a separate deny rule.',
    'Deny only the exposed port/protocol from the finding source CIDR (default 0.0.0.0/0 when public).',
    'Do not target entire host ranges when a single port is exposed.',
    'Set direction outbound only for egress findings; the revoke then targets the egress rule set.',
  ],
};

const GCP_FIREWALL_PROFILE: NetworkProviderCapabilityProfile = {
  applyModel: NETWORK_APPLY_MODELS.CREATE_DENY_RULE,
  requiredScopeType: 'firewall',
  requiresScopeId: false,
  supportedDirections: ['inbound'],
  constraints: [
    'GCP prevention creates a high-priority INGRESS deny firewall rule.',
    'Deny only the exposed port/protocol from the source CIDR.',
    'GCP egress deny cannot be scoped to a single host without instance tags, so outbound findings are not supported on this provider.',
  ],
};

const AZURE_NSG_PROFILE: NetworkProviderCapabilityProfile = {
  applyModel: NETWORK_APPLY_MODELS.CREATE_DENY_RULE,
  requiredScopeType: 'nsg',
  requiresScopeId: false,
  supportedDirections: ['inbound', 'outbound'],
  constraints: [
    'Azure prevention creates a Deny NSG security rule; direction inbound maps to Inbound and outbound to Outbound.',
    'Deny only the exposed port/protocol from the source CIDR.',
  ],
};

const NETWORK_PROVIDER_PROFILES: Partial<
  Record<PreventionProvider, NetworkProviderCapabilityProfile>
> = {
  awsSecurityGroup: AWS_SECURITY_GROUP_PROFILE,
  gcpFirewall: GCP_FIREWALL_PROFILE,
  azureNsg: AZURE_NSG_PROFILE,
};

export const resolveNetworkProviderProfile = (
  provider: PreventionProvider,
): NetworkProviderCapabilityProfile | null =>
  NETWORK_PROVIDER_PROFILES[provider] ?? null;

export const buildNetworkProviderConstraints = ({
  provider,
}: {
  provider: PreventionProvider;
}): string[] => [
  ...(resolveNetworkProviderProfile(provider)?.constraints ?? []),
];

export const collectNetworkProviderCapabilityViolations = ({
  spec,
  provider,
}: {
  spec: PreventionControlSpec;
  provider: PreventionProvider | null | undefined;
}): string[] => {
  if (spec.controlType !== PREVENTION_CONTROL_TYPES.NETWORK || !provider) {
    return [];
  }
  const profile = resolveNetworkProviderProfile(provider);
  if (!profile) {
    return [];
  }
  return collectNetworkSpecCapabilityViolations({ spec, profile });
};

const collectNetworkSpecCapabilityViolations = ({
  spec,
  profile,
}: {
  spec: NetworkControlSpec;
  profile: NetworkProviderCapabilityProfile;
}): string[] => {
  const violations: string[] = [];
  if (spec.action !== 'deny') {
    violations.push('Network prevention controls must use action deny.');
  }
  if (
    profile.requiresScopeId &&
    (!spec.scopeId || spec.scopeId.trim().length === 0)
  ) {
    violations.push(
      `${profile.applyModel === NETWORK_APPLY_MODELS.REVOKE_INGRESS ? 'AWS security group' : 'Network'} prevention requires scopeId when the security boundary is known.`,
    );
  }
  if (
    spec.scopeType &&
    profile.requiredScopeType &&
    spec.scopeType !== profile.requiredScopeType
  ) {
    violations.push(
      `scopeType "${spec.scopeType}" is incompatible with provider apply model ${profile.applyModel}.`,
    );
  }
  if (!profile.supportedDirections.includes(spec.direction)) {
    violations.push(
      `Network prevention direction "${spec.direction}" is not supported by provider apply model ${profile.applyModel}.`,
    );
  }
  if (
    profile.applyModel === NETWORK_APPLY_MODELS.REVOKE_INGRESS &&
    spec.scopeType === 'firewall'
  ) {
    violations.push(
      'AWS security group prevention cannot use scopeType firewall; use securityGroup.',
    );
  }
  return violations;
};

export const isNetworkProviderCapabilityViolationError = (
  error: unknown,
): boolean => {
  if (!(error instanceof Error)) {
    return false;
  }
  const message = error.message.toLowerCase();
  return (
    message.includes('network prevention') ||
    message.includes('scopeid') ||
    message.includes('security group') ||
    message.includes('apply model')
  );
};
